import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';

const VerificationPopup = () => {
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [rewardCredits, setRewardCredits] = useState(50);

  useEffect(() => {
    checkAndShowPopup();
  }, []);

  const checkAndShowPopup = async () => {
    try {
      const candidateSession = SessionManager.getSession('candidate');
      if (!candidateSession || !candidateSession.user_id) return;

      const userId = candidateSession.user_id;
      const email = candidateSession.email;
      const token = candidateSession.access_token;

      // Check if popup was dismissed
      const dismissedUntil = localStorage.getItem('verification_popup_dismissed_until');
      if (dismissedUntil) {
        const dismissedDate = new Date(dismissedUntil);
        if (dismissedDate > new Date()) {
          return;
        }
      }

      // ===== STEP 1: Check profile completion FIRST =====
      try {
        const profileRes = await fetch(`${API_BASE_URL}/candidates/profile?candidate_id=${userId}&email=${email || ''}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (profileRes.ok) {
          const profileData = await profileRes.json();
          const p = profileData?.profile || profileData;
          const fullName = p?.full_name || `${p?.first_name || ''} ${p?.last_name || ''}`.trim();
          const hasBasic = !!(fullName && p?.phone);
          const hasDetails = !!(
            (p?.education && p.education.length > 0) ||
            (p?.work_experience && p.work_experience.length > 0) ||
            (p?.skills && p.skills.length > 0)
          );
          const profileComplete = hasBasic && hasDetails;

          if (!profileComplete) {
            // Profile not complete — DO NOT show verification popup
            return;
          }
        } else {
          // Can't fetch profile — don't show popup
          return;
        }
      } catch {
        return;
      }

      // ===== STEP 2: Check verification status =====
      const response = await fetch(`${API_BASE_URL}/verification/status/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.verified) return; // Already verified

        // ===== STEP 3: Fetch reward credits from config =====
        try {
          const configRes = await fetch(`${API_BASE_URL}/verification/config`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (configRes.ok) {
            const configData = await configRes.json();
            if (configData.verification_reward_credits) {
              setRewardCredits(configData.verification_reward_credits);
            }
          }
        } catch {
          // Use default
        }

        // Show popup after delay
        const justCompletedProfile = sessionStorage.getItem('profile_just_completed');
        const delay = justCompletedProfile ? 500 : 3000;
        if (justCompletedProfile) {
          sessionStorage.removeItem('profile_just_completed');
        }

        setTimeout(() => {
          setShowPopup(true);
        }, delay);
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
    }
  };

  const handleVerifyNow = () => {
    setShowPopup(false);
    navigate('/verification-welcome');
  };

  const handleMaybeLater = () => {
    setShowPopup(false);
    if (dontShowAgain) {
      const dismissUntil = new Date();
      dismissUntil.setDate(dismissUntil.getDate() + 7);
      localStorage.setItem('verification_popup_dismissed_until', dismissUntil.toISOString());
    }
  };

  if (!showPopup) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-[#0a1929] via-blue-600 to-[#0a1929]" />

        <div className="p-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-[#0a1929] rounded-xl flex items-center justify-center flex-shrink-0">
              <Icon name="verified_user" size={28} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Verify Your Profile
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Stand out to recruiters with a verified badge
              </p>
            </div>
          </div>

          {/* Reward Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="stars" size={22} className="text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900">
                Earn {rewardCredits} Credits
              </p>
              <p className="text-xs text-amber-700">
                Complete verification and receive {rewardCredits} bonus credits to your account
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="trending_up" size={18} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">3x More Visibility</p>
                <p className="text-xs text-gray-500">Verified profiles get priority in recruiter searches</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="shield" size={18} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Build Employer Trust</p>
                <p className="text-xs text-gray-500">Demonstrate your commitment to professional growth</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-violet-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="timer" size={18} className="text-violet-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Quick Process</p>
                <p className="text-xs text-gray-500">Takes approximately 20-25 minutes to complete</p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 mb-5" />

          {/* Actions */}
          <div className="flex flex-col gap-2.5">
            <button
              onClick={handleVerifyNow}
              className="w-full py-3 px-4 bg-[#0a1929] hover:bg-[#0d2137] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Icon name="verified_user" size={18} />
              Start Verification
            </button>
            <button
              onClick={handleMaybeLater}
              className="w-full py-2.5 px-4 text-gray-500 hover:text-gray-700 hover:bg-gray-50 font-medium rounded-xl transition-colors text-sm"
            >
              Maybe Later
            </button>
          </div>

          {/* Don't show checkbox */}
          <div className="mt-4 flex items-center justify-center">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-3.5 h-3.5 text-[#0a1929] border-gray-300 rounded focus:ring-[#0a1929]"
              />
              <span className="text-xs text-gray-400">
                Don't remind me for 7 days
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationPopup;
